import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';

/**
 * useUserAddedBreweriesCount
 * Fetches user-added breweries from companyLocationRecords where source = 'userAdded'
 * and provides both total count and breakdown by state.
 */
function useUserAddedBreweriesCount() {
  const [count, setCount] = useState(0);
  const [stateBreakdown, setStateBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        setLoading(true);
        setError(null);

        const collRef = collection(db, 'companyLocationRecords');
        const qry = query(collRef, where('source', '==', 'userAdded'));
        const snap = await getDocs(qry);

        if (cancelled) return;

        const totalCount = snap.size;
        const stateMap = {};

        snap.forEach((docSnap) => {
          const data = docSnap.data();
          const state = data?.state || 'Unknown';
          stateMap[state] = (stateMap[state] || 0) + 1;
        });

        // Convert to array and sort by count (descending), then by state name
        const breakdown = Object.entries(stateMap)
          .map(([state, stateCount]) => ({ state, count: stateCount }))
          .sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return a.state.localeCompare(b.state);
          });

        setCount(totalCount);
        setStateBreakdown(breakdown);
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setCount(0);
          setStateBreakdown([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  return { count, stateBreakdown, loading, error };
}

export default useUserAddedBreweriesCount;
